import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Eye,
  MousePointer,
  Mail,
  DollarSign,
  Target,
  Lightbulb,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  Link,
  Globe,
  Loader2,
  Sparkles,
  BookOpen,
  Tag,
  Smile,
  FileText
} from 'lucide-react';
import { Card, Button } from '../ui';
import { NewsletterPost } from '@/types/newsletter';
import { useNewsletterStore } from '@/stores/newsletterStore';
import { newsletterService } from '@/services/newsletterService';

interface ContentAnalysis {
  wordCount: number;
  subjectLineLength: number;
  hasEmoji: boolean;
  sentimentScore: number;
  readabilityScore: number;
  primaryTopics: string[];
  keywords: string[];
  entities: string[];
  analysisDate: string;
}

interface NewsletterPostInsightsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  post: NewsletterPost | null;
}

const NewsletterPostInsightsDrawer: React.FC<NewsletterPostInsightsDrawerProps> = ({
  isOpen,
  onClose,
  post: initialPost
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'benchmarks' | 'insights' | 'clicks' | 'analysis'>('overview');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const { postsStats, loadPostBenchmarks, loadPostInsights, loadPostClicks } = useNewsletterStore();

  // Get the current post from the store (which may have updated details)
  const post = postsStats?.posts.find(p => p.id === initialPost?.id) || initialPost;

  // Load post details when drawer opens
  useEffect(() => {
    if (isOpen && post) {
      setActiveTab('overview');

      // Only load details if they haven't been loaded yet
      const needsDetails = !post.benchmarks || !post.insights || !post.clicks;

      if (needsDetails) {
        setLoadingDetails(true);

        // Load all details in parallel
        Promise.all([
          !post.benchmarks ? loadPostBenchmarks(post.id).catch(err => {
            console.warn('Failed to load benchmarks:', err);
            return null;
          }) : Promise.resolve(),
          !post.insights ? loadPostInsights(post.id).catch(err => {
            console.warn('Failed to load insights:', err);
            return null;
          }) : Promise.resolve(),
          !post.clicks ? loadPostClicks(post.id).catch(err => {
            console.warn('Failed to load clicks:', err);
            return null;
          }) : Promise.resolve()
        ]).finally(() => {
          setLoadingDetails(false);
        });
      }
    }
  }, [isOpen, post?.id]);

  if (!post) return null;

  // Format numbers with abbreviations
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  // Format EMV as currency
  const formatEMV = (value: number): string => {
    if (value >= 1000) {
      return `$${(value / 1000).toFixed(2)}K`;
    }
    return `$${value.toFixed(2)}`;
  };

  // Calculate percentages
  const openRate = post.stats.averageOpenRate;
  const clickRate = post.stats.averageClickRate;
  const deliveryRate = post.stats.totalDelivered > 0
    ? (post.stats.totalDelivered / post.stats.totalSent * 100)
    : 0;

  // Render benchmark comparison
  const renderBenchmarkMetric = (label: string, value: number, benchmark?: number) => {
    if (!benchmark) {
      return (
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <span className="text-sm font-semibold text-gray-900">{value.toFixed(1)}%</span>
        </div>
      );
    }

    const diff = value - benchmark;
    const isPositive = diff > 0;
    const isNeutral = Math.abs(diff) < 0.1;

    return (
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-gray-900">{value.toFixed(1)}%</span>
          {!isNeutral && (
            <div className={`flex items-center text-xs ${
              isPositive ? 'text-green-600' : 'text-red-600'
            }`}>
              {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              <span className="ml-1">{Math.abs(diff).toFixed(1)}%</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-75 z-[9999]"
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed right-0 top-16 bottom-0 w-2/3 max-w-4xl bg-white shadow-2xl z-[10000] overflow-hidden flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Newsletter Post Insights</h2>
                  <p className="text-sm text-gray-500">Performance metrics and AI Analysis</p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Navigation Tabs */}
            <div className="flex border-b border-gray-200 bg-gray-50 px-6">
              {[
                { id: 'overview', label: 'Overview', icon: Eye },
                { id: 'benchmarks', label: 'Benchmarks', icon: Target, disabled: !post.benchmarks },
                { id: 'insights', label: 'Insights', icon: Lightbulb, disabled: !post.insights },
                { id: 'analysis', label: 'Analysis', icon: Sparkles },
                { id: 'clicks', label: 'Clicks', icon: MousePointer, disabled: !post.clicks }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => !tab.disabled && setActiveTab(tab.id as any)}
                  disabled={tab.disabled}
                  className={`flex items-center px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                    activeTab === tab.id
                      ? 'text-blue-600 border-blue-600'
                      : tab.disabled
                        ? 'text-gray-400 cursor-not-allowed border-transparent'
                        : 'text-gray-600 hover:text-gray-900 border-transparent hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-6 space-y-6">
              {/* Loading Indicator */}
              {loadingDetails && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-blue-600 animate-spin mr-2" />
                  <span className="text-sm text-gray-600">Loading post details...</span>
                </div>
              )}

              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <Card variant="elevated" padding="lg">
                    {/* Key Metrics */}
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Key Metrics
                    </h4>
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <DollarSign className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatEMV(post.stats.emv)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Earned Media Value</div>
                      </div>
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <Mail className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.stats.totalSent)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Emails Sent</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <Eye className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.stats.totalUniqueOpened)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Unique Opens</div>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <MousePointer className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.stats.totalClicked)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total Clicks</div>
                      </div>
                    </div>

                    {/* Performance Rates */}
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Performance Rates</h5>
                    <div className="space-y-3 mb-6">
                      {renderBenchmarkMetric('Open Rate', openRate)}
                      {renderBenchmarkMetric('Click Rate', clickRate)}
                      {renderBenchmarkMetric('Delivery Rate', deliveryRate)}
                    </div>

                    {/* Delivery Statistics */}
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Delivery Statistics</h5>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Total Sent</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatNumber(post.stats.totalSent)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Total Delivered</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatNumber(post.stats.totalDelivered)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700">Bounced</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatNumber(post.stats.totalSent - post.stats.totalDelivered)}
                        </span>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}

              {/* Benchmarks Tab */}
              {activeTab === 'benchmarks' && post.benchmarks && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <Card variant="elevated" padding="lg">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Industry Benchmarks
                    </h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Compare your post's performance against industry standards.
                    </p>
                    {post.benchmarks.availableMetrics.length > 0 ? (
                      <div className="space-y-4">
                        {post.benchmarks.availableMetrics.map((metric) => {
                          const benchmarkData = post.benchmarks?.benchmarks[metric];
                          if (!benchmarkData || benchmarkData.length === 0) return null;

                          return (
                            <div key={metric} className="border-t pt-4">
                              <h5 className="text-sm font-medium text-gray-900 mb-2 capitalize">
                                {metric.replace(/([A-Z])/g, ' $1').trim()}
                              </h5>
                              <div className="space-y-2">
                                {benchmarkData.map((benchmark) => (
                                  <div key={benchmark.id} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">
                                      {benchmark.percentile}th Percentile
                                    </span>
                                    <span className="font-semibold text-gray-900">
                                      {benchmark.benchmarkValue.toFixed(2)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No benchmark data available for this post.</p>
                    )}
                  </Card>
                </motion.div>
              )}

              {/* Insights Tab */}
              {activeTab === 'insights' && post.insights && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <Card variant="elevated" padding="lg">
                    {post.insights?.insights?.all && post.insights.insights.all.length > 0 ? (
                      <>
                        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                          Insight Summary
                        </h4>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                          <div className="text-center p-4 bg-blue-50 rounded-lg">
                            <Lightbulb className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-gray-900">
                              {post.insights.summary.totalInsights}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">Total Insights</div>
                          </div>
                          <div className="text-center p-4 bg-green-50 rounded-lg">
                            <TrendingUp className="h-6 w-6 text-green-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-gray-900">
                              {(() => {
                                const score = post.insights?.summary?.averageConfidenceScore;
                                if (score == null || score === undefined || score === 0) return 'N/A';
                                const numericScore = typeof score === 'number' ? score : parseFloat(String(score));
                                if (isNaN(numericScore) || numericScore === 0) return 'N/A';
                                return `${(numericScore * 100).toFixed(0)}%`;
                              })()}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">Avg Confidence</div>
                          </div>
                          <div className="text-center p-4 bg-orange-50 rounded-lg">
                            <Target className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                            <div className="text-2xl font-bold text-gray-900">
                              {post.insights.summary.priorityBreakdown?.high || 0}
                            </div>
                            <div className="text-xs text-gray-600 mt-1">High Priority</div>
                          </div>
                        </div>

                        <h5 className="text-sm font-semibold text-gray-700 mb-3">AI-Generated Insights</h5>
                        <div className="space-y-4">
                          {post.insights.insights.all.map((insight) => (
                            <div
                              key={insight.id}
                              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <h6 className="text-base font-semibold text-gray-900">{insight.title}</h6>
                                    {insight.insightsPriority && (
                                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                        insight.insightsPriority === 'high'
                                          ? 'bg-red-100 text-red-700'
                                          : insight.insightsPriority === 'medium'
                                            ? 'bg-yellow-100 text-yellow-700'
                                            : 'bg-green-100 text-green-700'
                                      }`}>
                                        {insight.insightsPriority.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-xs text-gray-500">
                                    {insight.insightType && (
                                      <>
                                        <span className="capitalize">{insight.insightType.replace(/_/g, ' ')}</span>
                                        <span>•</span>
                                      </>
                                    )}
                                    <span>Confidence: {(() => {
                                      const score = insight.confidenceScore;
                                      if (score == null || score === undefined) return 'N/A';
                                      const numericScore = typeof score === 'number' ? score : parseFloat(String(score));
                                      if (isNaN(numericScore)) return 'N/A';
                                      return `${(numericScore * 100).toFixed(0)}%`;
                                    })()}</span>
                                  </div>
                                </div>
                              </div>

                              <p className="text-sm text-gray-700 mb-4 leading-relaxed">
                                {insight.insightsDescription}
                              </p>

                              {insight.metrics && Object.keys(insight.metrics).length > 0 && (
                                <div className="mb-4 p-3 bg-white rounded border border-gray-200">
                                  <h6 className="text-xs font-semibold text-gray-700 uppercase mb-2">Metrics</h6>
                                  <div className="grid grid-cols-2 gap-2">
                                    {Object.entries(insight.metrics).map(([key, value]) => (
                                      <div key={key} className="flex justify-between text-xs">
                                        <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
                                        <span className="font-medium text-gray-900">
                                          {typeof value === 'number' && value < 1
                                            ? `${(value * 100).toFixed(0)}%`
                                            : value}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {insight.recommendations && insight.recommendations.length > 0 && (
                                <div className="space-y-2">
                                  <h6 className="text-xs font-semibold text-gray-700 uppercase mb-2">Recommendations</h6>
                                  {insight.recommendations.map((rec, idx) => (
                                    <div key={idx} className="p-3 bg-white rounded border border-blue-200">
                                      <div className="flex items-center gap-2 mb-1">
                                        {rec.priority && (
                                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                            rec.priority === 'high'
                                              ? 'bg-red-100 text-red-700'
                                              : rec.priority === 'medium'
                                                ? 'bg-yellow-100 text-yellow-700'
                                                : 'bg-blue-100 text-blue-700'
                                          }`}>
                                            {rec.priority.toUpperCase()}
                                          </span>
                                        )}
                                        {rec.effort && (
                                          <span className="text-xs text-gray-500">
                                            Effort: {rec.effort}
                                          </span>
                                        )}
                                      </div>
                                      {rec.action && <p className="text-sm text-gray-700 mb-2">{rec.action}</p>}
                                      {rec.expected_impact && (
                                        <p className="text-xs text-gray-600 italic">
                                          Expected Impact: {rec.expected_impact}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12">
                        <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500">No insights available for this post.</p>
                      </div>
                    )}
                  </Card>
                </motion.div>
              )}

              {/* Analysis Tab */}
              {activeTab === 'analysis' && (
                <AnalysisTabContent postId={post.id} />
              )}

              {/* Clicks Tab */}
              {activeTab === 'clicks' && post.clicks && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-6"
                >
                  <Card variant="elevated" padding="lg">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4">
                      Click Summary
                    </h4>
                    <div className="grid grid-cols-4 gap-4 mb-6">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <Link className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {post.clicks.summary.totalUrls}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total URLs</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <MousePointer className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.clicks.summary.totalClicks)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Total Clicks</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <Mail className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.clicks.summary.totalEmailClicks)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Email Clicks</div>
                      </div>
                      <div className="text-center p-4 bg-orange-50 rounded-lg">
                        <Globe className="h-6 w-6 text-orange-600 mx-auto mb-2" />
                        <div className="text-2xl font-bold text-gray-900">
                          {formatNumber(post.clicks.summary.totalWebClicks)}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">Web Clicks</div>
                      </div>
                    </div>

                    <h5 className="text-sm font-semibold text-gray-700 mb-3">Top Clicked URLs</h5>
                    {post.clicks.clickData.mostRecentByUrl.length > 0 ? (
                      <div className="space-y-3">
                        {post.clicks.clickData.mostRecentByUrl.slice(0, 10).map((click) => (
                          <div key={click.id} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-start justify-between mb-2">
                              <a
                                href={click.postClicksUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center flex-1 mr-2"
                              >
                                <span className="truncate">{click.postClicksUrl}</span>
                                <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
                              </a>
                              <span className="text-sm font-semibold text-gray-900">
                                {formatNumber(click.totalClicks)}
                              </span>
                            </div>
                            <div className="flex items-center text-xs text-gray-500 space-x-3">
                              <span>Domain: {click.clickDomain}</span>
                              {click.utmSource && <span>UTM Source: {click.utmSource}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No click data available.</p>
                    )}
                  </Card>
                </motion.div>
              )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Analysis Tab Content Component
interface AnalysisTabContentProps {
  postId: string;
}

const AnalysisTabContent: React.FC<AnalysisTabContentProps> = ({ postId }) => {
  const [analysis, setAnalysis] = useState<ContentAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAnalysis = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await newsletterService.getPostContentAnalysis(postId);

        if (response.success && response.data?.latestAnalysis) {
          setAnalysis(response.data.latestAnalysis);
        } else {
          setAnalysis(null);
        }
      } catch (err) {
        console.error('Failed to load content analysis:', err);
        setError('Failed to load content analysis');
        setAnalysis(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadAnalysis();
  }, [postId]);

  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-center py-12"
      >
        <Loader2 className="h-8 w-8 text-blue-600 animate-spin mr-2" />
        <span className="text-sm text-gray-600">Loading content analysis...</span>
      </motion.div>
    );
  }

  if (error || !analysis) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12"
      >
        <Sparkles className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">
          {error || 'No content analysis available for this post'}
        </p>
      </motion.div>
    );
  }

  const getSentimentColor = (score: number) => {
    if (score >= 0.6) return 'text-green-600';
    if (score >= 0.4) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSentimentLabel = (score: number) => {
    if (score >= 0.6) return 'Positive';
    if (score >= 0.4) return 'Neutral';
    return 'Negative';
  };

  const getReadabilityLabel = (score: number) => {
    if (score >= 80) return 'Very Easy';
    if (score >= 60) return 'Easy';
    if (score >= 40) return 'Moderate';
    if (score >= 20) return 'Difficult';
    return 'Very Difficult';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Content Metrics */}
      <Card variant="elevated" padding="lg">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
          <BookOpen className="h-5 w-5 mr-2 text-blue-600" />
          Content Metrics
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-3xl font-bold text-blue-900">{analysis.wordCount.toLocaleString()}</p>
            <p className="text-sm text-blue-700 font-medium mt-1">Word Count</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
            <p className="text-3xl font-bold text-purple-900">{analysis.subjectLineLength}</p>
            <p className="text-sm text-purple-700 font-medium mt-1">Subject Length</p>
          </div>
        </div>
      </Card>

      {/* Sentiment & Readability */}
      <Card variant="elevated" padding="lg">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
          <Smile className="h-5 w-5 mr-2 text-yellow-600" />
          Sentiment & Readability
        </h4>
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Sentiment Score</span>
              <span className={`text-sm font-bold ${getSentimentColor(analysis.sentimentScore)}`}>
                {getSentimentLabel(analysis.sentimentScore)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className={`h-3 rounded-full ${
                  analysis.sentimentScore >= 0.6 ? 'bg-green-500' :
                  analysis.sentimentScore >= 0.4 ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                style={{ width: `${analysis.sentimentScore * 100}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{(analysis.sentimentScore * 100).toFixed(1)}%</p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Readability Score</span>
              <span className="text-sm font-bold text-indigo-900">
                {getReadabilityLabel(analysis.readabilityScore)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-indigo-500 h-3 rounded-full"
                style={{ width: `${analysis.readabilityScore}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">{analysis.readabilityScore.toFixed(1)}/100</p>
          </div>

          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <span className="text-sm font-medium text-gray-600">Contains Emoji</span>
            <span className={`text-sm font-bold ${analysis.hasEmoji ? 'text-green-600' : 'text-gray-500'}`}>
              {analysis.hasEmoji ? '✓ Yes' : '✗ No'}
            </span>
          </div>
        </div>
      </Card>

      {/* Topics */}
      {analysis.primaryTopics && analysis.primaryTopics.length > 0 && (
        <Card variant="elevated" padding="lg">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2 text-indigo-600" />
            Primary Topics
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.primaryTopics.map((topic, index) => (
              <span
                key={index}
                className="px-3 py-2 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium"
              >
                {topic}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Keywords */}
      {analysis.keywords && analysis.keywords.length > 0 && (
        <Card variant="elevated" padding="lg">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
            <Tag className="h-5 w-5 mr-2 text-green-600" />
            Keywords
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.keywords.map((keyword, index) => (
              <span
                key={index}
                className="px-3 py-2 bg-green-100 text-green-700 rounded-full text-sm font-medium"
              >
                {keyword}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Entities */}
      {analysis.entities && analysis.entities.length > 0 && (
        <Card variant="elevated" padding="lg">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-4 flex items-center">
            <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
            Entities
          </h4>
          <div className="flex flex-wrap gap-2">
            {analysis.entities.map((entity, index) => (
              <span
                key={index}
                className="px-3 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium"
              >
                {entity}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Analysis Date */}
      <div className="text-center text-sm text-gray-500">
        Last analyzed: {new Date(analysis.analysisDate).toLocaleDateString()}
      </div>
    </motion.div>
  );
};

export default NewsletterPostInsightsDrawer;
